/*
  <vz-vehicle-finder> — the finder UI.

  Presentation only. Every matching decision comes from vz-fitment-core.js; this
  file decides what to show and when. That split is deliberate: the finder can be
  redesigned completely without touching a line of matching logic.

  Two behaviours here are worth understanding before changing anything.

  1. THE DECODE INDICATOR HAS A SINGLE OWNER. setLoading() is the only thing that
     touches the in-flight state, and EVERY exit path out of decode() calls it —
     including the error paths. Miss one and the shopper is left watching a
     spinner that never stops, which is worse than no spinner at all.

  2. AMBIGUITY IS NOT AN ERROR. When several engines survive, the dropdown is
     narrowed to exactly those candidates and searching happens the moment one is
     picked. Returning "no engine" when candidates exist is a dead end.

  2026-09-02 (bug sheet 31-08):
  · VZB-003  Searching still saves the car to the garage — that is what makes
             the PDP fitment messages work — but the results page now says so.
             #announceSave() leaves a one-shot note in sessionStorage that
             vz-collection.js turns into a toast with an Undo.
  · VZB-008  The VIN field cleans itself as you type: uppercase, and anything
             that can never appear in a VIN (I, O, Q, punctuation, spaces) is
             dropped on input rather than complained about on submit.

  2026-10-04 (QA sheet 30-09):
  · UAE-0016 Only a COMPLETE vehicle (make + model + year + engine) is saved to
             the garage. A Make + Model search still shows the matching parts,
             but nothing is written to the garage or the store-wide lock; the
             results page shows "Add year and engine to save this car".
             Vehicle names use vehicleName() ("Range Rover Sport").
*/

// '?v=' is a manual cache-buster: relative imports bypass Shopify's asset_url
// hash and the CDN caches the bare path for a year. BUMP IT (here, in
// vz-garage-ui.js and in vz-recs.js — keep all three identical so the browser
// loads ONE copy of the module) every time vz-fitment-core.js changes.
import {
  makes, models, years, engines, codesFor,
  validateVin, vehicleFromDecode, searchUrl, applyVehicleToSearch, vehicleName,
} from './vz-fitment-core.js?v=20261004a';

import { readVehicle, readList, saveVehicle, vehicleKey } from './vz-garage.js';

const PLACEHOLDER_ORDER = ['make', 'model', 'year', 'engine'];

// Read once by vz-collection.js on the results page, then removed.
const GARAGE_NOTE_KEY = 'vz_garage_note';

// The store-wide lock: the finished filter query for the locked vehicle, replayed
// onto every collection and search URL by snippets/vz-vehicle-lock.liquid.
// Written here and by vz-garage-ui.js; removed by every "clear" control.
const GARAGE_QUERY_KEY = 'vz_garage_query';

// Everything a VIN may contain. I, O and Q are excluded by the standard
// because they are confusable with 1 and 0.
const VIN_ALLOWED = /[^A-HJ-NPR-Z0-9]/g;

/* 2026-10-13 (VIN fitment): a successful VIN decode is kept for the session so
   assets/vz-vin-filter.js can check every product's custom.fits_epc (EPC
   catalogue + From/To VIN + page) against THIS car on the results page, and
   the results URL carries filter.p.m.custom.fits_vin = the VIN's model-year
   letter (10th character) so the server-side filter already drops products
   whose EPC ranges never cover that model year. Cleared with the vehicle. */
const VIN_DECODE_KEY = 'vz_vin_decode';
const FITS_VIN_PARAM = 'filter.p.m.custom.fits_vin';

class VzVehicleFinder extends HTMLElement {
  connectedCallback() {
    this.strings = this.#json('strings') || {};
    this.config = this.#json('config') || {};

    this.fields = {
      make: this.querySelector('[data-field="make"]'),
      model: this.querySelector('[data-field="model"]'),
      year: this.querySelector('[data-field="year"]'),
      engine: this.querySelector('[data-field="engine"]'),
    };

    this.searchButton = this.querySelector('[data-action="search"]');
    this.resetButton = this.querySelector('[data-action="reset"]');
    this.vinInput = this.querySelector('[data-vin-input]');
    this.vinButton = this.querySelector('[data-vin-submit]');
    this.vinBar = this.querySelector('[data-vin-bar]');
    this.message = this.querySelector('[data-message]');

    // Engine codes resolved from a VIN. When set, the query filters on codes
    // rather than the engine label — see the ALL rule in vz-fitment-core.
    this.resolvedCodes = null;
    this.abortController = null;

    this.#populate(this.fields.make, makes(), 'make');
    this.#disable('model');
    this.#disable('year');
    this.#disable('engine');

    this.#bind();
    this.#restoreFromGarage();
    this.#syncSearchButton();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  /* ------------------------------------------------------------- helpers -- */

  #json(name) {
    const node = this.querySelector(`[data-${name}]`);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  #placeholder(field) {
    return (this.strings.placeholders || {})[field] || '';
  }

  #populate(select, values, field) {
    select.innerHTML = '';
    select.appendChild(new Option(this.#placeholder(field), ''));
    for (const value of values) {
      const label = (this.strings.makeLabels || {})[value] || value;
      select.appendChild(new Option(label, value));
    }
    select.disabled = false;
  }

  #disable(field) {
    const select = this.fields[field];
    select.innerHTML = '';
    select.appendChild(new Option(this.#placeholder(field), ''));
    select.disabled = true;
  }

  /* Clears every field after the one that changed. Selecting a new make must
     not leave last selection's model sitting there looking valid. */
  #clearAfter(field) {
    const index = PLACEHOLDER_ORDER.indexOf(field);
    for (const later of PLACEHOLDER_ORDER.slice(index + 1)) this.#disable(later);
    this.resolvedCodes = null;
  }

  get #selection() {
    return {
      make: this.fields.make.value,
      model: this.fields.model.value,
      year: this.fields.year.value ? parseInt(this.fields.year.value, 10) : null,
      engine: this.fields.engine.value || null,
      codes: this.resolvedCodes,
    };
  }

  #syncSearchButton() {
    const ready = Boolean(this.fields.make.value && this.fields.model.value);
    this.searchButton.disabled = !ready;
  }

  #url() {
    const selection = this.#selection;
    if (!selection.make || !selection.model) return null;
    return searchUrl(selection, this.config.collection, this.config.root);
  }

  #say(html, tone) {
    if (!this.message) return;
    this.message.className = 'vz-finder__message';
    if (tone) this.message.classList.add(`vz-finder__message--${tone}`);
    this.message.innerHTML = html || '';
  }

  #whatsappLink(vin) {
    const number = this.config.whatsapp;
    if (!number) return '';
    const text = encodeURIComponent(`${this.strings.whatsappText || 'VIN: '}${vin}`);
    const label = this.strings.whatsappLink || 'WhatsApp';
    return ` <a href="https://wa.me/${number}?text=${text}" target="_blank" rel="noopener">${label}</a>`;
  }

  /* The raw engine string, shown when we could not identify an engine. Makes the
     next unmatched vehicle diagnosable from a screenshot. */
  #detail(text) {
    if (!text) return '';
    const escaped = String(text).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
    return ` <span class="vz-finder__detail">(${escaped})</span>`;
  }

  /*
    Save the selection to the garage and leave a note for the results page.

    The note says whether the car was NEW to the garage, because the toast
    differs: a new car gets "Saved … · Undo"; a car already in the garage just
    gets "Now shopping for …" — offering Undo there would delete something the
    shopper saved on purpose earlier.
  */
  /* A vehicle the garage can use for fitment checks: all four fields. */
  #isComplete(selection) {
    return Boolean(selection.make && selection.model && selection.year && selection.engine);
  }

  /*
    Called by every search (button and VIN auto-search). A complete vehicle is
    saved as before. A partial one (Make + Model, or no engine yet) is NOT
    saved: the shopper still gets the filtered results, and the results page
    is told to explain how to save the car (QA UAE-0016).
  */
  #remember(selection) {
    if (this.#isComplete(selection)) {
      this.#announceSave(selection);
      return;
    }
    try {
      window.sessionStorage.setItem(GARAGE_NOTE_KEY, JSON.stringify({
        make: selection.make,
        model: selection.model,
        year: selection.year || null,
        engine: selection.engine || null,
        notSaved: true,
      }));
    } catch (error) {
      /* no sessionStorage — results still show, there is just no hint */
    }
  }

  #announceSave(selection) {
    const key = vehicleKey({
      make: selection.make,
      model: selection.model,
      year: selection.year ? String(selection.year) : '',
      engine: selection.engine || '',
    });
    const isNew = !readList().some((entry) => vehicleKey(entry) === key);

    saveVehicle(selection);

    // Arm the store-wide lock with the exact query this search is about to use.
    try {
      window.localStorage.setItem(GARAGE_QUERY_KEY, this.#withVinLetter(applyVehicleToSearch('', selection)));
    } catch (error) {
      /* no localStorage — the results page is still filtered, other pages are not */
    }

    try {
      window.sessionStorage.setItem(GARAGE_NOTE_KEY, JSON.stringify({
        make: selection.make,
        model: selection.model,
        year: selection.year || null,
        engine: selection.engine || null,
        isNew,
      }));
    } catch (error) {
      /* no sessionStorage — the car is still saved, there is just no toast */
    }
  }

  /* ------------------------------------------------------------- binding -- */

  #bind() {
    this.fields.make.addEventListener('change', () => {
      this.vinLetter = null;
      this.#clearAfter('make');
      if (this.fields.make.value) {
        this.#populate(this.fields.model, models(this.fields.make.value), 'model');
      }
      this.#syncSearchButton();
    });

    this.fields.model.addEventListener('change', () => {
      this.#clearAfter('model');
      const { make, model } = this.#selection;
      if (make && model) {
        this.#populate(this.fields.year, years(make, model).map(String), 'year');
      }
      this.#syncSearchButton();
    });

    this.fields.year.addEventListener('change', () => {
      this.#clearAfter('year');
      const { make, model, year } = this.#selection;
      if (make && model && year) {
        this.#populate(this.fields.engine, engines(make, model, year), 'engine');
      }
      this.#syncSearchButton();
    });

    this.fields.engine.addEventListener('change', () => {
      // A hand-picked engine invalidates any codes a previous VIN resolved.
      this.resolvedCodes = null;
      this.#syncSearchButton();
    });

    this.searchButton.addEventListener('click', () => {
      // A hand-picked car (no fresh VIN decode) must not inherit an old VIN's checks.
      if (!this.vinLetter) { try { window.sessionStorage.removeItem(VIN_DECODE_KEY); } catch (e) { /* ignore */ } }
      this.#go();
    });

    this.resetButton.addEventListener('click', () => {
      this.fields.make.value = '';
      this.#clearAfter('make');
      this.#say('');
      if (this.vinInput) this.vinInput.value = '';
      this.vinLetter = null;
      try { window.sessionStorage.removeItem(VIN_DECODE_KEY); } catch (e) { /* ignore */ }
      this.#syncSearchButton();
    });

    if (this.vinInput && this.vinButton) {
      this.vinInput.addEventListener('input', () => this.#cleanVin());
      this.vinButton.addEventListener('click', () => this.#decode());
      this.vinInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.#decode();
      });
    }
  }

  /*
    Keep the VIN field honest as it is typed (VZB-008).

    Uppercases and strips anything outside the VIN alphabet — that covers i/o/q
    in either case, spaces pasted from a document, and hyphens. The caret is
    put back where it was, minus the characters removed before it, so typing
    in the middle of a VIN does not jump to the end.

    A short note is shown when one of I/O/Q was dropped, because silently eating
    a keystroke reads as a broken keyboard. Any other message on screen is left
    alone.
  */
  #cleanVin() {
    const input = this.vinInput;
    const raw = input.value;
    const clean = raw.toUpperCase().replace(VIN_ALLOWED, '');
    if (clean === raw) return;

    const caret = input.selectionStart;
    const before = raw.slice(0, caret);
    const removedBefore = before.length - before.toUpperCase().replace(VIN_ALLOWED, '').length;

    input.value = clean;
    try {
      input.setSelectionRange(caret - removedBefore, caret - removedBefore);
    } catch (error) {
      /* some input types refuse selection ranges; the value is still clean */
    }

    if (/[IOQ]/i.test(raw) && this.strings.vinChars) {
      this.#say(this.strings.vinChars, 'warning');
    }
  }

  #go() {
    const url = this.#url();
    if (!url) return;
    this.#remember(this.#selection);
    window.location.href = url;
  }

  /* ------------------------------------------------------- garage restore -- */

  #restoreFromGarage() {
    const saved = readVehicle();
    if (!saved || !saved.make) return;
    if (!makes().includes(saved.make)) return;

    this.fields.make.value = saved.make;
    this.#populate(this.fields.model, models(saved.make), 'model');

    if (!saved.model || !models(saved.make).includes(saved.model)) return;
    this.fields.model.value = saved.model;
    this.#populate(this.fields.year, years(saved.make, saved.model).map(String), 'year');

    if (!saved.year) return;
    this.fields.year.value = String(saved.year);
    this.#populate(this.fields.engine, engines(saved.make, saved.model, saved.year), 'engine');

    if (saved.engine) this.fields.engine.value = saved.engine;
  }

  /* -------------------------------------------------------- decode state -- */

  /*
    The only place the in-flight state is set or cleared.

    The button keeps its width while loading (the label is hidden with
    visibility rather than removed) so the bar cannot reflow mid-decode. The
    input goes readOnly rather than disabled, because disabling drops focus and
    on iOS closes the keyboard — jarring if the decode fails and the shopper
    needs to fix a typo.
  */
  #setLoading(loading) {
    this.vinBar.classList.toggle('is-loading', loading);
    this.vinButton.classList.toggle('is-loading', loading);
    this.vinButton.disabled = loading;
    this.vinButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    this.vinInput.readOnly = loading;
  }

  async #decode() {
    const check = validateVin(this.vinInput.value);
    this.vinInput.value = check.vin;
    this.resolvedCodes = null;

    if (!check.valid) {
      const reason = check.reason === 'not-jlr' ? 'vinNotJlr' : 'vinInvalid';
      this.#say(this.strings[reason], 'error');
      return;
    }

    if (!this.config.decodeUrl) {
      console.warn('[vz-finder] VIN decode endpoint not configured');
      this.#say(this.#failMessage(check.vin), 'error');
      return;
    }

    this.#say(this.strings.vinDecoding);
    this.#setLoading(true);

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    let payload;
    try {
      const response = await fetch(this.config.decodeUrl, {
        method: 'POST',
        headers: this.#decodeHeaders(),
        body: JSON.stringify({ vin: check.vin, catalog_code: check.catalog }),
        signal: this.abortController.signal,
      });
      payload = await response.json().catch(() => null);

      if (!payload || payload.success !== true || !payload.data || !payload.data.length) {
        const why = (payload && payload.message) || `HTTP ${response.status}`;
        this.#setLoading(false);
        console.warn('[vz-finder] VIN decode failed:', why);
        this.#say(this.#failMessage(check.vin), 'error');
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') return; // superseded by a newer decode
      this.#setLoading(false);
      console.warn('[vz-finder] VIN decode failed: network', error);
      this.#say(this.#failMessage(check.vin), 'error');
      return;
    }

    this.#setLoading(false);
    this.#storeDecode(payload.data[0], check.vin);
    this.#applyDecode(vehicleFromDecode(payload.data[0], check.make), check.vin);
  }

  /* Keep what the decoder said about THIS car (VIN fitment, 13 Oct 2026). */
  #storeDecode(d, vin) {
    try {
      const m = String(d.description || '').match(/\(([LX]\d{3})\)/);
      window.sessionStorage.setItem(VIN_DECODE_KEY, JSON.stringify({
        vin,
        serial: vin.slice(9),
        letter: vin.charAt(9),
        cat: m ? m[1] : '',
        catalogue: d.description || '',
        plant: d.build_plant || '',
        engine: d.engine_type || '',
        wheelbase: d.wheelbase || '',
        year: d.model_year || '',
        t: Date.now(),
      }));
      this.vinLetter = vin.charAt(9);
    } catch (error) {
      /* no sessionStorage — the results page simply skips the exact VIN check */
    }
  }

  /* Add the VIN model-year letter to a results query (plus ALL, for products
     that carry no EPC VIN ranges yet). */
  #withVinLetter(query) {
    if (!this.vinLetter) return query;
    const params = new URLSearchParams(query);
    params.delete(FITS_VIN_PARAM);
    params.append(FITS_VIN_PARAM, this.vinLetter);
    params.append(FITS_VIN_PARAM, 'ALL');
    return params.toString();
  }
  /* Customer-facing decode failure (QA UAE-0011 / UK-0001).
     The raw API reason goes to the console only, never to the shopper. */
  #failMessage(vin) {
    const wa = this.#whatsappLink(vin);
    const help = wa ? `<br><span class="vz-finder__help-line">${this.strings.vinHelp || ''}${wa}</span>` : '';
    return `${this.strings.vinFailed}${help}`;
  }
  #decodeHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    // Only sent on a direct call. Behind an app proxy the key stays server-side,
    // which is where it belongs.
    if (this.config.apiKey) headers['X-API-Key'] = this.config.apiKey;
    return headers;
  }

  /* --------------------------------------------------------- decode result -- */

  #applyDecode(result, vin) {
    if (!result.resolved) {
      // Show what we DID learn, so the shopper is not sent back to square one.
      if (result.make) {
        this.fields.make.value = result.make;
        this.#populate(this.fields.model, models(result.make), 'model');
        if (result.model && models(result.make).includes(result.model)) {
          this.fields.model.value = result.model;
          this.#populate(this.fields.year, years(result.make, result.model).map(String), 'year');
        }
        this.#syncSearchButton();
      }

      const detail = result.description || result.engineType;
      this.#say(this.strings.vinOutOfScope + this.#detail(detail) + this.#whatsappLink(vin), 'warning');
      return;
    }

    const { make, model, year, engine } = result;

    this.fields.make.value = make;
    this.#populate(this.fields.model, models(make), 'model');
    this.fields.model.value = model;
    this.#populate(this.fields.year, years(make, model).map(String), 'year');

    if (year) {
      this.fields.year.value = String(year);
      this.#populate(this.fields.engine, engines(make, model, year), 'engine');
      if (engine && engine.label) this.fields.engine.value = engine.label;
    }

    this.resolvedCodes = engine && engine.codes ? engine.codes : null;
    this.#syncSearchButton();

    const description = [
      vehicleName(make, model, document.documentElement.lang),
      year,
      engine && engine.label,
    ].filter(Boolean).join(' · ');

    // Ambiguous: the decode worked, we simply cannot tell which of N engines it
    // is. Narrow to the real candidates and search the moment one is picked.
    if (result.ambiguous && year) {
      this.#populate(this.fields.engine, engine.candidates, 'engine');
      this.#say((this.strings.vinPick || '%s').replace('%s', `<b>${description}</b>`), 'warning');

      this.fields.engine.addEventListener('change', () => {
        if (!this.fields.engine.value) return;
        this.resolvedCodes = codesFor(make, model, this.fields.engine.value);
        this.#syncSearchButton();
        if (this.config.autoSearch !== false) this.#go();
      }, { once: true });

      try { this.fields.engine.focus(); } catch (error) { /* focus is best-effort */ }
      return;
    }

    const template = result.complete ? this.strings.vinFound : this.strings.vinPartial;
    let text = (template || '%s').replace('%s', `<b>${description}</b>`);
    if (!result.complete && result.engineType) text += this.#detail(result.engineType);

    if (this.config.autoSearch === false) {
      this.#say(text, result.complete ? 'success' : 'warning');
      return;
    }

    let target = this.#url();
    if (!target) {
      this.#say(text, result.complete ? 'success' : 'warning');
      return;
    }
    if (this.vinLetter && target.indexOf('?') > -1) {
      const [path, query] = target.split('?');
      target = `${path}?${this.#withVinLetter(query)}`;
    }

    this.#remember(this.#selection);
    this.#say(`${text} <span class="vz-finder__going">${this.strings.vinGoing || ''}</span>`,
      result.complete ? 'success' : 'warning');

    // Hold the loading state through the redirect pause. Clearing it here and
    // navigating a second later reads as two separate stalls.
    this.#setLoading(true);
    this.searchButton.disabled = true;

    const delay = typeof this.config.autoSearchDelay === 'number' ? this.config.autoSearchDelay : 1200;
    setTimeout(() => { window.location.href = target; }, delay);
  }
}

if (!customElements.get('vz-vehicle-finder')) {
  customElements.define('vz-vehicle-finder', VzVehicleFinder);
}